import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Save, Plus, ChevronDown, Calendar as CalendarIcon } from "lucide-react";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Custom Dropdown Component matching the modern UI requested
const CreatableDropdown = ({ value, options, onChange, onCreate, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newVal, setNewVal] = useState("");
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsCreating(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCreate = async () => {
    if (!newVal.trim()) return;
    await onCreate(newVal.trim());
    setIsCreating(false);
    setIsOpen(false);
    setNewVal("");
    setSearch("");
  };

  const selectedOption = options.find(opt => opt.id === value);
  const displayValue = selectedOption ? selectedOption.name : "";
  const filteredOptions = options.filter(opt => opt.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="relative" ref={dropdownRef}>
      <div 
        className={`w-full px-4 py-2 border rounded-lg bg-white flex justify-between items-center cursor-pointer transition-colors ${isOpen ? 'border-indigo-500 ring-2 ring-indigo-500/20' : 'border-gray-300 hover:border-indigo-400'}`}
        onClick={() => {
          if (!isCreating) {
            setIsOpen(!isOpen);
            if (!isOpen) setSearch(""); // Reset search when opening
          }
        }}
      >
        <span className={displayValue ? "text-gray-900" : "text-gray-400"}>
          {displayValue || placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
      </div>

      {/* Hidden input to enable native HTML5 form validation */}
      <input 
        type="text" 
        value={value || ""} 
        onChange={() => {}} 
        required 
        className="opacity-0 absolute bottom-0 left-1/2 -translate-x-1/2 w-[1px] h-[1px] -z-10 pointer-events-none" 
        tabIndex={-1} 
      />

      {isOpen && (
        <div className="absolute z-50 w-full mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden flex flex-col max-h-80">
          
          {/* Search Bar */}
          {!isCreating && (
            <div className="p-2 border-b border-gray-100 bg-gray-50">
              <input
                type="text"
                autoFocus
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500/40 transition-shadow"
              />
            </div>
          )}

          {isCreating ? (
            <div className="p-2 flex items-center space-x-2 border-b border-gray-100 bg-indigo-50/50">
              <input
                type="text"
                autoFocus
                value={newVal}
                onChange={(e) => setNewVal(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleCreate())}
                placeholder="Type new name..."
                className="w-full px-3 py-1.5 text-sm border border-indigo-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
              <button type="button" onClick={handleCreate} className="px-3 py-1.5 bg-indigo-600 text-white text-sm font-bold rounded-md hover:bg-indigo-700 shrink-0 shadow-sm transition-colors">
                Save
              </button>
              <button 
                type="button" 
                onClick={() => { setIsCreating(false); setNewVal(""); }} 
                className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm font-bold rounded-md hover:bg-gray-300 shrink-0 shadow-sm transition-colors"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div 
              className="px-4 py-2 border-b border-gray-100 bg-white hover:bg-indigo-50 text-indigo-600 font-semibold cursor-pointer flex items-center transition-colors text-sm"
              onClick={() => { setIsCreating(true); setNewVal(search); }}
            >
              <Plus className="w-4 h-4 mr-2" />
              {search ? `Create "${search}"` : "Create New..."}
            </div>
          )}

          <div className="overflow-y-auto py-1">
            {filteredOptions.map((opt) => (
              <div
                key={opt.id}
                className={`px-4 py-2 text-sm cursor-pointer transition-colors ${value === opt.id ? 'bg-indigo-50 text-indigo-900 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}
                onClick={() => {
                  onChange(opt.id);
                  setIsOpen(false);
                }}
              >
                {opt.name}
              </div>
            ))}
            {filteredOptions.length === 0 && !isCreating && (
              <div className="px-4 py-3 text-sm text-gray-400 italic text-center">No matches found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const initialFormData = {
  award_name: "",
  award_type_id: "",
  category_id: "",
  criteria: "",
  nomination_process: "",
  is_active: "1",
  nomination_fee: "",
  gst_percentage: "",
  nomination_start_date: "",
  nomination_end_date: "",
  voting_start_date: "",
  voting_end_date: "",
  winner_date: "",
  winner_pop_up_end: "",
  sort_order: "",
  prize: "",
};

const AddAwardModal = ({ isOpen, onClose, apiCall, onAwardAdded, editData }) => {
  const [formData, setFormData] = useState(initialFormData);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [dropdowns, setDropdowns] = useState({ types: [], categories: [] });

  const fetchDropdowns = async () => {
    try {
      // Fetch the dropdown data
      const data = await apiCall("/awards/dropdowns/types_category");
      if (data) {
        setDropdowns({
          types: data.types || [],
          categories: data.categories || []
        });
      }
    } catch (error) {
      console.error("Failed to fetch dropdowns:", error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (editData) {
        setFormData({
          award_name: editData.award_name || "",
          award_type_id: editData.award_type_id || "",
          category_id: editData.category_id || "",
          criteria: editData.criteria || "",
          nomination_process: editData.nomination_process || "",
          is_active: editData.is_active || "1",
          nomination_fee: editData.nomination_fee !== null ? editData.nomination_fee : "",
          gst_percentage: editData.gst_percentage !== null ? editData.gst_percentage : "",
          nomination_start_date: editData.nomination_start_date || "",
          nomination_end_date: editData.nomination_end_date || "",
          voting_start_date: editData.voting_start_date || "",
          voting_end_date: editData.voting_end_date || "",
          winner_date: editData.winner_date || "",
          winner_pop_up_end: editData.winner_pop_up_end || "",
          sort_order: editData.sort_order !== null && editData.sort_order !== undefined ? editData.sort_order : "",
          prize: editData.prize || "",
        });
      } else {
        setFormData(initialFormData);
      }
      setErrorMsg("");
      fetchDropdowns();
    }
  }, [isOpen, editData]);

  const handleCreateType = async (name) => {
    try {
      setLoading(true);
      const res = await apiCall("/awards/types/create", {
        method: "POST",
        body: JSON.stringify({ name }),
      });
      await fetchDropdowns();
      if (res && res.id) {
        setFormData(prev => ({ ...prev, award_type_id: res.id }));
        toast.success("Award Type created!");
      }
      setErrorMsg("");
    } catch (error) {
      setErrorMsg(error.message || "Failed to create award type");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async (name) => {
    try {
      setLoading(true);
      const res = await apiCall("/awards/categories/create", {
        method: "POST",
        body: JSON.stringify({ name }),
      });
      await fetchDropdowns();
      if (res && res.id) {
        setFormData(prev => ({ ...prev, category_id: res.id }));
        toast.success("Category created!");
      }
      setErrorMsg("");
    } catch (error) {
      setErrorMsg(error.message || "Failed to create category");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Strict manual validation to catch anything HTML5 validation might miss
    if (!formData.award_name || !formData.award_name.trim()) {
      toast.error("Award Name is required.");
      return;
    }
    if (!formData.award_type_id) {
      toast.error("Award Type is required.");
      return;
    }
    if (!formData.category_id) {
      toast.error("Category is required.");
      return;
    }


    try {
      setLoading(true);
      setErrorMsg("");

      const { nomination_start_date, nomination_end_date, voting_start_date, voting_end_date } = formData;

      // Date logic validations
      if (nomination_start_date && nomination_end_date && new Date(nomination_end_date) < new Date(nomination_start_date)) {
        throw new Error("Nomination End Date cannot be before Start Date.");
      }
      if (voting_start_date && voting_end_date && new Date(voting_end_date) < new Date(voting_start_date)) {
        throw new Error("Voting End Date cannot be before Start Date.");
      }

      // Format dates to be either valid ISO strings or null if empty
      const payload = { ...formData };
      
      // Set optional numerical/text fields to null if empty
      if (payload.nomination_fee === "") {
        payload.nomination_fee = null;
      } else if (payload.nomination_fee !== null) {
        payload.nomination_fee = parseFloat(payload.nomination_fee);
      }

      if (payload.gst_percentage === "") {
        payload.gst_percentage = null;
      } else if (payload.gst_percentage !== null) {
        payload.gst_percentage = parseFloat(payload.gst_percentage);
      }

      if (payload.prize === "") {
        payload.prize = null;
      }

      if (payload.sort_order !== "" && payload.sort_order !== null && payload.sort_order !== undefined) {
        payload.sort_order = parseInt(payload.sort_order, 10);
      } else {
        payload.sort_order = null;
      }
      
      const dateFields = [
        "nomination_start_date",
        "nomination_end_date",
        "voting_start_date",
        "voting_end_date",
        "winner_date",
        "winner_pop_up_end"
      ];
      
      dateFields.forEach(field => {
        if (!payload[field]) {
          payload[field] = null;
        } else {
          const d = new Date(payload[field]);
          const pad = n => n < 10 ? '0' + n : n;
          // Format as local time string: YYYY-MM-DDTHH:mm:ss.000Z 
          // (Adding Z so backend accepts it if it strictly requires ISO format, but keeping local time values)
          payload[field] = d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) + 'T' + pad(d.getHours()) + ':' + pad(d.getMinutes()) + ':00.000Z';
        }
      });

      if (editData) {
        const originalData = {
          award_name: editData.award_name || "",
          award_type_id: editData.award_type_id || "",
          category_id: editData.category_id || "",
          criteria: editData.criteria || "",
          nomination_process: editData.nomination_process || "",
          is_active: editData.is_active || "1",
          nomination_fee: editData.nomination_fee !== null ? editData.nomination_fee : "",
          gst_percentage: editData.gst_percentage !== null ? editData.gst_percentage : "",
          nomination_start_date: editData.nomination_start_date || "",
          nomination_end_date: editData.nomination_end_date || "",
          voting_start_date: editData.voting_start_date || "",
          voting_end_date: editData.voting_end_date || "",
          winner_date: editData.winner_date || "",
          winner_pop_up_end: editData.winner_pop_up_end || "",
          sort_order: editData.sort_order !== null && editData.sort_order !== undefined ? editData.sort_order : "",
          prize: editData.prize || "",
        };

        dateFields.forEach(field => {
          if (!originalData[field]) {
            originalData[field] = null;
          } else {
            const d = new Date(originalData[field]);
            const pad = n => n < 10 ? '0' + n : n;
            originalData[field] = d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) + 'T' + pad(d.getHours()) + ':' + pad(d.getMinutes()) + ':00.000Z';
          }
        });

        const changedPayload = {};
        Object.keys(payload).forEach(key => {
          if (String(payload[key]) !== String(originalData[key])) {
             changedPayload[key] = payload[key];
          }
        });

        if (Object.keys(changedPayload).length === 0) {
          toast.info("No changes made.");
          setLoading(false);
          return;
        }

        await apiCall(`/awards/update/${editData.id}`, {
          method: "PUT",
          body: JSON.stringify(changedPayload),
        });
        toast.success("Award updated successfully!");
      } else {
        await apiCall("/awards/create", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        toast.success("Award created successfully!");
      }

      onAwardAdded(); // Refresh the list
      onClose();      // Close modal
    } catch (error) {
      console.error("Error creating award:", error);
      setErrorMsg(error.message || `Failed to ${editData ? "update" : "create"} award`);
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm overflow-hidden" style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0 }}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] h-auto flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-5 md:p-6 border-b border-gray-100 bg-gray-50/50 shrink-0">
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">{editData ? "Edit Award" : "Add New Award"}</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <div className="p-5 md:p-6 overflow-y-auto flex-1 min-h-0">
          <form id="add-award-form" onSubmit={handleSubmit} className="space-y-5">
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Details */}
              <div className="col-span-1 md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Award Name *</label>
                <input
                  type="text"
                  name="award_name"
                  required
                  value={formData.award_name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                  placeholder="e.g. Best Educational Institute"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Award Type *</label>
                <CreatableDropdown
                  value={formData.award_type_id}
                  options={dropdowns.types}
                  onChange={(val) => setFormData(prev => ({ ...prev, award_type_id: val }))}
                  onCreate={handleCreateType}
                  placeholder="Select award type..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Category *</label>
                <CreatableDropdown
                  value={formData.category_id}
                  options={dropdowns.categories}
                  onChange={(val) => setFormData(prev => ({ ...prev, category_id: val }))}
                  onCreate={handleCreateCategory}
                  placeholder="Select category..."
                />
              </div>

              <div className="col-span-1 md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Criteria</label>
                <textarea
                  name="criteria"
                  rows="3"
                  value={formData.criteria}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                  placeholder="Describe the criteria for this award..."
                ></textarea>
              </div>

              <div className="col-span-1 md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Nomination Process</label>
                <textarea
                  name="nomination_process"
                  rows="2"
                  value={formData.nomination_process}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                  placeholder="How does someone get nominated?"
                ></textarea>
              </div>

              {/* Financials & Status */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Nomination Fee (₹)</label>
                <input
                  type="number"
                  name="nomination_fee"
                  min="0"
                  step="0.01"
                  value={formData.nomination_fee}
                  onChange={handleChange}
                  onWheel={(e) => e.target.blur()}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">GST Percentage (%)</label>
                <input
                  type="number"
                  name="gst_percentage"
                  min="0"
                  max="99"
                  value={formData.gst_percentage}
                  onChange={(e) => {
                    if (e.target.value.length > 2) return;
                    handleChange(e);
                  }}
                  onKeyDown={(e) => {
                    if (['.', 'e', 'E', '+', '-'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onWheel={(e) => e.target.blur()}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
                <div className="flex items-center space-x-3 mt-2">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, is_active: prev.is_active === "1" ? "0" : "1" }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none shadow-inner ${
                      formData.is_active === "1" ? 'bg-indigo-500' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                        formData.is_active === "1" ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className={`text-sm font-bold ${formData.is_active === "1" ? 'text-indigo-600' : 'text-gray-500'}`}>
                    {formData.is_active === "1" ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Sort Order</label>
                <input
                  type="number"
                  name="sort_order"
                  min="0"
                  value={formData.sort_order}
                  onChange={handleChange}
                  onWheel={(e) => e.target.blur()}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                  placeholder="e.g. 1"
                />
              </div>

              <div className="col-span-1 md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Prize (Optional)</label>
                <textarea
                  name="prize"
                  rows="2"
                  value={formData.prize}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                  placeholder="e.g. $1000 or Trophy"
                ></textarea>
              </div>
            </div>

            <div className="border-t border-gray-200 my-6 pt-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Dates & Timelines</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Nomination Start Date</label>
                  <div className="relative">
                    <DatePicker
                      selected={formData.nomination_start_date ? new Date(formData.nomination_start_date) : null}
                      onChange={(date) => setFormData(prev => ({ ...prev, nomination_start_date: date }))}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      dateFormat="MMMM d, yyyy HH:mm"
                      minDate={new Date()}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                      wrapperClassName="w-full"
                      placeholderText="Select start date & time"
                    />
                    <CalendarIcon className="w-4 h-4 text-gray-400 absolute left-3 top-3 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Nomination End Date</label>
                  <div className="relative">
                    <DatePicker
                      selected={formData.nomination_end_date ? new Date(formData.nomination_end_date) : null}
                      onChange={(date) => setFormData(prev => ({ ...prev, nomination_end_date: date }))}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      dateFormat="MMMM d, yyyy HH:mm"
                      minDate={formData.nomination_start_date ? new Date(formData.nomination_start_date) : new Date()}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                      wrapperClassName="w-full"
                      placeholderText="Select end date & time"
                    />
                    <CalendarIcon className="w-4 h-4 text-gray-400 absolute left-3 top-3 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Voting Start Date</label>
                  <div className="relative">
                    <DatePicker
                      selected={formData.voting_start_date ? new Date(formData.voting_start_date) : null}
                      onChange={(date) => setFormData(prev => ({ ...prev, voting_start_date: date }))}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      dateFormat="MMMM d, yyyy HH:mm"
                      minDate={new Date()}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                      wrapperClassName="w-full"
                      placeholderText="Select voting start date"
                    />
                    <CalendarIcon className="w-4 h-4 text-gray-400 absolute left-3 top-3 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Voting End Date</label>
                  <div className="relative">
                    <DatePicker
                      selected={formData.voting_end_date ? new Date(formData.voting_end_date) : null}
                      onChange={(date) => setFormData(prev => ({ ...prev, voting_end_date: date }))}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      dateFormat="MMMM d, yyyy HH:mm"
                      minDate={formData.voting_start_date ? new Date(formData.voting_start_date) : new Date()}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                      wrapperClassName="w-full"
                      placeholderText="Select voting end date"
                    />
                    <CalendarIcon className="w-4 h-4 text-gray-400 absolute left-3 top-3 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Winner Announcement Date</label>
                  <div className="relative">
                    <DatePicker
                      selected={formData.winner_date ? new Date(formData.winner_date) : null}
                      onChange={(date) => setFormData(prev => ({ ...prev, winner_date: date }))}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      dateFormat="MMMM d, yyyy HH:mm"
                      minDate={formData.voting_end_date ? new Date(formData.voting_end_date) : new Date()}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                      wrapperClassName="w-full"
                      placeholderText="Select winner announcement date"
                    />
                    <CalendarIcon className="w-4 h-4 text-gray-400 absolute left-3 top-3 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Winner Popup End Date</label>
                  <div className="relative">
                    <DatePicker
                      selected={formData.winner_pop_up_end ? new Date(formData.winner_pop_up_end) : null}
                      onChange={(date) => setFormData(prev => ({ ...prev, winner_pop_up_end: date }))}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      dateFormat="MMMM d, yyyy HH:mm"
                      minDate={formData.winner_date ? new Date(formData.winner_date) : new Date()}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                      wrapperClassName="w-full"
                      placeholderText="Select popup end date"
                    />
                    <CalendarIcon className="w-4 h-4 text-gray-400 absolute left-3 top-3 pointer-events-none" />
                  </div>
                </div>
              </div>
            </div>

          </form>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 bg-gray-50 flex items-center shrink-0">
          {errorMsg && (
            <div className="text-red-500 text-sm font-semibold mr-auto">
              {errorMsg}
            </div>
          )}
          <div className="flex space-x-4 ml-auto">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 rounded-lg text-sm font-bold text-gray-600 hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
          <button
            type="submit"
            form="add-award-form"
            disabled={loading}
            className={`flex items-center px-6 py-2 rounded-lg text-sm font-bold text-white transition-colors ${
              loading ? "bg-indigo-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700 shadow-md"
            }`}
          >
            {loading ? (
              <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
            ) : (
              <Save className="w-5 h-5 mr-2" />
            )}
            {loading ? "Saving..." : editData ? "Update Award" : "Create Award"}
          </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddAwardModal;
